import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useMediaQuery,
  Avatar,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';

const JournalHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // ===== Auth state =====
  const [authUser, setAuthUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const isAuthenticated = Boolean(token && authUser);

  const syncAuthFromStorage = () => {
    try {
      const raw = localStorage.getItem('user');
      setAuthUser(raw ? JSON.parse(raw) : null);
      setToken(localStorage.getItem('token'));
    } catch {
      setAuthUser(null);
      setToken(null);
    }
  };


  useEffect(() => {
    // initial read
    syncAuthFromStorage();

    // updates from other tabs/windows
    const onStorage = () => syncAuthFromStorage();
    window.addEventListener('storage', onStorage);

    // updates in this same tab (fire this event after login/logout)
    const onAuthUpdated = () => syncAuthFromStorage();
    window.addEventListener('auth:updated', onAuthUpdated);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:updated', onAuthUpdated);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('token_type'); // if you stored it
    localStorage.removeItem('user');
    setAuthUser(null);
    setToken(null);
    window.dispatchEvent(new Event('auth:updated')); // notify this tab's listeners
    navigate('/login');
  };

  const initials = (authUser?.name || authUser?.email || 'U')
    .split(' ')
    .map((s) => s[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

  // ===== Drawers & Menus =====
  const [topDrawerOpen, setTopDrawerOpen] = useState(false);
  const [bottomDrawerOpen, setBottomDrawerOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [guidelineOpen, setGuidelineOpen] = useState(false);

  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const aboutOpenMenu = Boolean(aboutAnchorEl);
  const handleAboutOpen = (e) => setAboutAnchorEl(e.currentTarget);
  const handleAboutClose = () => setAboutAnchorEl(null);

  const [issueAnchorEl, setIssueAnchorEl] = useState(null);
  const issueOpenMenu = Boolean(issueAnchorEl);
  const handleIssueOpen = (e) => setIssueAnchorEl(e.currentTarget);
  const handleIssueClose = () => setIssueAnchorEl(null);

  const [guidelinesAnchorEl, setGuidelinesAnchorEl] = useState(null);
  const guidelinesOpenMenu = Boolean(guidelinesAnchorEl);
  const handleGuidelinesOpen = (e) => setGuidelinesAnchorEl(e.currentTarget);
  const handleGuidelinesClose = () => setGuidelinesAnchorEl(null);

  // Profile menu
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(profileAnchorEl);
  const openProfileMenu = (e) => setProfileAnchorEl(e.currentTarget);
  const closeProfileMenu = () => setProfileAnchorEl(null);

  

  return (
    <Box>
      {/* Top Navbar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a8d3b' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Sabudhdhi Research</Typography>

          {isMobile ? (
            <>
              <IconButton edge="end" color="inherit" onClick={() => setTopDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>

              <Drawer anchor="right" open={topDrawerOpen} onClose={() => setTopDrawerOpen(false)}>
                <List sx={{ width: 260 }}>
                  <ListItem button component={Link} to="/home" onClick={() => setTopDrawerOpen(false)}>
                    <ListItemText primary="Home" />
                  </ListItem>

                  {/* About Submenu */}
                  <ListItem button onClick={() => setAboutOpen(!aboutOpen)}>
                    <ListItemText primary="About" />
                    {aboutOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={aboutOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem button component={Link} to="/about-journal" sx={{ pl: 4 }} onClick={() => setTopDrawerOpen(false)}>About the Research</ListItem>
                      <ListItem button component={Link} to="/index" sx={{ pl: 4 }} onClick={() => setTopDrawerOpen(false)}>Index</ListItem>
                      <ListItem button component={Link} to="/editorial-board" sx={{ pl: 4 }} onClick={() => setTopDrawerOpen(false)}>Editorial Board</ListItem>
                      <ListItem button component={Link} to="/submission-guidelines" sx={{ pl: 4 }} onClick={() => setTopDrawerOpen(false)}>Submission</ListItem>
                      <ListItem button component={Link} to="/contact-us" sx={{ pl: 4 }} onClick={() => setTopDrawerOpen(false)}>Contact Us</ListItem>
                    </List>
                  </Collapse>

                  {!isAuthenticated ? (
                    <>
                      <ListItem button component={Link} to="/register" onClick={() => setTopDrawerOpen(false)}>
                        <ListItemText primary="Register" />
                      </ListItem>
                      <ListItem button component={Link} to="/login" onClick={() => setTopDrawerOpen(false)}>
                        <ListItemText primary="Login" />
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <Divider />
                      <ListItem>
                        <Avatar sx={{ mr: 1 }}>{initials}</Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{authUser?.name || 'Profile'}</Typography>
                          <Typography variant="caption">{authUser?.email}</Typography>
                        </Box>
                      </ListItem>
                      {/* Open modal instead of navigating */}
                      
                      {/* Optional: Dashboard */}
                      {/* <ListItem button component={Link} to="/dashboard" onClick={() => setTopDrawerOpen(false)}>
                        <ListItemText primary="Dashboard" />
                      </ListItem> */}
                      <ListItem
                        button
                        onClick={() => {
                          setTopDrawerOpen(false);
                          logout();
                        }}
                      >
                        <ListItemText primary="Logout" />
                      </ListItem>
                    </>
                  )}
                </List>
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button component={Link} to="/home" sx={{ color: '#fff' }}>Home</Button>

              {/* About Desktop Menu */}
              <Box onMouseEnter={handleAboutOpen} onMouseLeave={handleAboutClose}>
                <Button sx={{ color: '#fff' }} endIcon={<ExpandMoreIcon />}>About</Button>
                <Menu
                  anchorEl={aboutAnchorEl}
                  open={aboutOpenMenu}
                  onClose={handleAboutClose}
                  MenuListProps={{ onMouseLeave: handleAboutClose }}
                >
                  <MenuItem component={Link} to="/about-journal">About the Research</MenuItem>
                  <MenuItem component={Link} to="/index">Index</MenuItem>
                  <MenuItem component={Link} to="/editorial-board">Editorial Board</MenuItem>
                  <MenuItem component={Link} to="/submission-guidelines">Submission</MenuItem>
                  <MenuItem component={Link} to="/contact-us">Contact Us</MenuItem>
                </Menu>
              </Box>

              {!isAuthenticated ? (
                <>
                  <Button component={Link} to="/register" sx={{ color: '#fff' }}>Register</Button>
                  <Button component={Link} to="/login" sx={{ color: '#fff' }}>Login</Button>
                </>
              ) : (
                <>
                  <IconButton onClick={openProfileMenu} sx={{ ml: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>{initials}</Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={profileAnchorEl}
                    open={profileMenuOpen}
                    onClose={closeProfileMenu}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  >
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="subtitle2">{authUser?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{authUser?.email}</Typography>
                    </Box>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        closeProfileMenu();
                        logout();
                      }}
                    >
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Middle Section */}
      <Box sx={{ backgroundColor: '#cde1d7', py: 2 }}>
        <Container>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Sabudhdhi Research
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
            An International Peer-Reviewed
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
            Multidisciplinary Research Journal
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '15px' }}>
            ISSN - 3093-6179
          </Typography>
        </Container>
      </Box>

      {/* Bottom Navbar */}
      <Box sx={{ backgroundColor: '#88a999', py: 1 }}>
        <Container>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {isMobile ? (
                <>
                  <IconButton edge="start" sx={{ color: '#fff' }} onClick={() => setBottomDrawerOpen(true)}>
                    <MenuIcon />
                  </IconButton>

                  <Drawer anchor="right" open={bottomDrawerOpen} onClose={() => setBottomDrawerOpen(false)}>
                    <List sx={{ width: 260 }}>
                      {/* Issue Submenu */}
                      <ListItem button onClick={() => setIssueOpen(!issueOpen)}>
                        <ListItemText primary="Issue" />
                        {issueOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={issueOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <ListItem button component={Link} to="/" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Current Issue</ListItem>
                          <ListItem button component={Link} to="/past-issues" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Past Issues</ListItem>
                          <ListItem button component={Link} to="/special-issue" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Special Issue</ListItem>
                        </List>
                      </Collapse>

                      <ListItem button component={Link} to="/archives" onClick={() => setBottomDrawerOpen(false)}>Archives</ListItem>
                      <ListItem button component={Link} to="/announcements" onClick={() => setBottomDrawerOpen(false)}>Announcements</ListItem>

                      {/* Guidelines Submenu */}
                      <ListItem button onClick={() => setGuidelineOpen(!guidelineOpen)}>
                        <ListItemText primary="Guidelines" />
                        {guidelineOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={guidelineOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <ListItem button component={Link} to="/author-guidelines" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Author Guidelines</ListItem>
                          <ListItem button component={Link} to="/submission-guidelines" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Submission Guidelines</ListItem>
                          <ListItem button component={Link} to="/review-process" sx={{ pl: 4 }} onClick={() => setBottomDrawerOpen(false)}>Peer Review Process</ListItem>
                        </List>
                      </Collapse>

                      <ListItem button component={Link} to="/article-fee" onClick={() => setBottomDrawerOpen(false)}>Article Fee</ListItem>
                      <ListItem button component={Link} to="/policy" onClick={() => setBottomDrawerOpen(false)}>Policy of Research</ListItem>
                      {isAuthenticated && (
                        <ListItem component={Link} to="/my-research" onClick={() => setBottomDrawerOpen(false)}>
                          My Research
                        </ListItem>
                      )}
                    </List>
                  </Drawer>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box onMouseEnter={handleIssueOpen} onMouseLeave={handleIssueClose}>
                    <Button sx={{ color: '#fff' }} endIcon={<ExpandMoreIcon />}>Issue</Button>
                    <Menu
                      anchorEl={issueAnchorEl}
                      open={issueOpenMenu}
                      onClose={handleIssueClose}
                      MenuListProps={{ onMouseLeave: handleIssueClose }}
                    >
                      <MenuItem component={Link} to="/">Current Issue</MenuItem>
                      <MenuItem component={Link} to="/past-issues">Past Issues</MenuItem>
                      <MenuItem component={Link} to="/special-issue">Special Issue</MenuItem>
                    </Menu>
                  </Box>

                  <Button component={Link} to="/archives" sx={{ color: '#fff' }}>Archives</Button>
                  <Button component={Link} to="/announcements" sx={{ color: '#fff' }}>Announcements</Button>

                  <Box onMouseEnter={handleGuidelinesOpen} onMouseLeave={handleGuidelinesClose}>
                    <Button sx={{ color: '#fff' }} endIcon={<ExpandMoreIcon />}>Guidelines</Button>
                    <Menu
                      anchorEl={guidelinesAnchorEl}
                      open={guidelinesOpenMenu}
                      onClose={handleGuidelinesClose}
                      MenuListProps={{ onMouseLeave: handleGuidelinesClose }}
                    >
                      <MenuItem component={Link} to="/author-guidelines">Author Guidelines</MenuItem>
                      <MenuItem component={Link} to="/submission-guidelines">Submission Guidelines</MenuItem>
                      <MenuItem component={Link} to="/review-process">Peer Review Process</MenuItem>
                    </Menu>
                  </Box>

                  <Button component={Link} to="/article-fee" sx={{ color: '#fff' }}>Article Fee</Button>
                  <Button component={Link} to="/policy" sx={{ color: '#fff' }}>Policy of Research</Button>
                  {isAuthenticated && (
                    <Button component={Link} to="/my-research" sx={{ color: '#fff' }}>My Research</Button>
                  )}
                </Box>
              )}
            </Grid>

            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center,', color: '#fff' }}>
                <SearchIcon sx={{ mr: 1 }} />
                <Typography>Search</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default JournalHeader;
